/**
 * Industry Terminology
 * ====================
 * Single synchronous source of truth for the nouns each industry uses, so the
 * SAME dashboard templates speak the client's own language (a hospitality
 * venue has "Guests", a clinic has "Patients", a law firm has "Clients").
 *
 * Mirrors the backend `/api/live-industry-config/{id}/terminology` values but
 * is synchronous to avoid per-widget fetch flicker. Always returns a complete
 * term set via the neutral `general` default — never hospitality.
 *
 * Keys: customer (singular), revenue, employee, plus derived `customers`
 * (plural) and `customerLower` helpers.
 */

const TERMS = {
  general:       { customer: 'Customer', revenue: 'Revenue',    employee: 'Team Member' },
  hospitality:   { customer: 'Guest',    revenue: 'Takings',    employee: 'Team Member' },
  accommodation: { customer: 'Guest',    revenue: 'Takings',    employee: 'Team Member' },
  healthcare:    { customer: 'Patient',  revenue: 'Billings',   employee: 'Practitioner' },
  veterinary:    { customer: 'Client',   revenue: 'Billings',   employee: 'Vet' },
  pharmacy:      { customer: 'Patient',  revenue: 'Sales',      employee: 'Pharmacist' },
  legal:         { customer: 'Client',   revenue: 'Fees',       employee: 'Fee Earner' },
  accounting:    { customer: 'Client',   revenue: 'Fees',       employee: 'Team Member' },
  professional:  { customer: 'Client',   revenue: 'Fees',       employee: 'Consultant' },
  finance:       { customer: 'Client',   revenue: 'Revenue',    employee: 'Adviser' },
  property:      { customer: 'Client',   revenue: 'Commission', employee: 'Agent' },
  retail:        { customer: 'Customer', revenue: 'Sales',      employee: 'Team Member' },
  wholesale:     { customer: 'Customer', revenue: 'Sales',      employee: 'Team Member' },
  construction:  { customer: 'Client',   revenue: 'Revenue',    employee: 'Tradesperson' },
  trades:        { customer: 'Client',   revenue: 'Revenue',    employee: 'Tradesperson' },
  home_services: { customer: 'Customer', revenue: 'Revenue',    employee: 'Technician' },
  automotive:    { customer: 'Customer', revenue: 'Sales',      employee: 'Technician' },
  technology:    { customer: 'Customer', revenue: 'Revenue',    employee: 'Team Member' },
  media:         { customer: 'Client',   revenue: 'Revenue',    employee: 'Creative' },
  fitness:       { customer: 'Member',   revenue: 'Revenue',    employee: 'Trainer' },
  beauty:        { customer: 'Client',   revenue: 'Takings',    employee: 'Stylist' },
  education:     { customer: 'Student',  revenue: 'Fees',       employee: 'Educator' },
  childcare:     { customer: 'Family',   revenue: 'Fees',       employee: 'Educator' },
  manufacturing: { customer: 'Customer', revenue: 'Revenue',    employee: 'Operator' },
  mining:        { customer: 'Customer', revenue: 'Revenue',    employee: 'Worker' },
  utilities:     { customer: 'Customer', revenue: 'Revenue',    employee: 'Technician' },
  transport:     { customer: 'Customer', revenue: 'Revenue',    employee: 'Driver' },
  agriculture:   { customer: 'Buyer',    revenue: 'Revenue',    employee: 'Worker' },
  events:        { customer: 'Client',   revenue: 'Revenue',    employee: 'Team Member' },
  nonprofit:     { customer: 'Member',   revenue: 'Income',     employee: 'Team Member' },
  public_admin:  { customer: 'Resident', revenue: 'Funding',    employee: 'Officer' },
  admin_services:{ customer: 'Client',   revenue: 'Revenue',    employee: 'Team Member' },
};

// Aliases → canonical terminology key.
const ALIASES = {
  qsr: 'hospitality',
  food: 'hospitality',
  restaurant: 'hospitality',
  cafe: 'hospitality',
  hotel: 'accommodation',
  medical: 'healthcare',
  dental: 'healthcare',
  it_services: 'technology',
  saas: 'technology',
  software: 'technology',
  it: 'technology',
  law: 'legal',
  realestate: 'property',
  'real estate': 'property',
  gym: 'fitness',
  wellness: 'fitness',
  salon: 'beauty',
  logistics: 'transport',
  pet_services: 'veterinary',
  senior_care: 'healthcare',
  'professional services': 'professional',
  consulting: 'professional',
};

const _plural = (word) => {
  if (!word) return 'Customers';
  if (word === 'Family') return 'Families';
  if (/[sxz]$/i.test(word) || /(ch|sh)$/i.test(word)) return `${word}es`;
  if (/[^aeiou]y$/i.test(word)) return `${word.slice(0, -1)}ies`;
  return `${word}s`;
};

/**
 * Returns the full terminology set for an industry, always complete.
 * @param {string} industry - raw industry key/label
 */
export function getIndustryTerms(industry) {
  const raw = (industry || '').toString().toLowerCase().trim();
  const key = TERMS[raw] ? raw : (ALIASES[raw] || 'general');
  const base = TERMS[key] || TERMS.general;
  const customers = _plural(base.customer);
  return {
    ...base,
    key,
    customers,
    customerLower: base.customer.toLowerCase(),
    customersLower: customers.toLowerCase(),
  };
}

/**
 * Returns the canonical industry key (resolving aliases like it_services →
 * technology). Defaults to 'general' — NEVER hospitality. Use this anywhere a
 * component keys industry-specific data so non-hospitality tenants never fall
 * back to hospitality content.
 * @param {string} industry - raw industry key/label
 */
export function resolveIndustryKey(industry) {
  const raw = (industry || '').toString().toLowerCase().trim();
  return TERMS[raw] ? raw : (ALIASES[raw] || 'general');
}

export default getIndustryTerms;
